import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IconBase } from '../../assets/images/icons/icon_base.svg';
import { ReactComponent as IconBatteryCharging } from '../../assets/images/icons/icon_battery_charging.svg';
import { ReactComponent as IconCellular } from '../../assets/images/icons/icon_cellular.svg';
import { ReactComponent as IconConnected } from '../../assets/images/icons/icon_connected.svg';
import { ReactComponent as IconDrip } from '../../assets/images/icons/icon_drip.svg';
import { ReactComponent as IconFeather } from '../../assets/images/icons/icon_feather.svg';
import { ReactComponent as IconLight } from '../../assets/images/icons/icon_light.svg';
import { ReactComponent as IconMagic } from '../../assets/images/icons/icon_magic.svg';
import { ReactComponent as IconReinforced } from '../../assets/images/icons/icon_reinforced.svg';
import { ReactComponent as IconVibration } from '../../assets/images/icons/icon_vibration.svg';
import { ReactComponent as IconCheckCircle } from '../../assets/images/icons/check_circle_filled.svg';
import { ReactComponent as IconStar } from '../../assets/images/icons/star.svg';
import AppPaths from '../../AppPaths';
import { productListingPage, quizQuestionPage } from '../../lib/analytics/events';
import { useLaunchQuiz } from '../../lib/fiQuiz';
import type { PickYourFiDeviceKey } from './HardwareFirstBelowFold';
import styles from './HardwareFirstSections.module.scss';

const DEVICE_NAMES: Record<PickYourFiDeviceKey, string> = {
  mini: 'Fi Mini',
  s3: 'Fi Series 3+',
  ultra: 'Fi Ultra',
};

interface SpecRow {
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  titleKey: string;
  subKey: string;
}

// row content mirrors each device's standalone-pdp product summary, restyled per the figma
const SPEC_ROWS: Record<PickYourFiDeviceKey, SpecRow[]> = {
  mini: [
    { Icon: IconDrip, titleKey: 'fully_waterproof', subKey: 'ip68_tested_in_salt_water_no_dash' },
    { Icon: IconFeather, titleKey: 'small_and_versatile', subKey: 'can_be_added_to_any_collar' },
    { Icon: IconBatteryCharging, titleKey: 'up_to_2_months_battery_life', subKey: 'on_a_single_usb_c_charge' },
    { Icon: IconMagic, titleKey: 'featuring_fi_intelligence', subKey: 'ask_fi_anything_your_dog_decoded' },
    { Icon: IconCellular, titleKey: 'hardware_first_spec_verizon', subKey: 'verizon_lte_m_cellular_network' },
  ],
  s3: [
    { Icon: IconDrip, titleKey: 'fully_waterproof', subKey: 'ip68_66k_tested_in_salt_water_up_to_50_meters_no_period' },
    { Icon: IconReinforced, titleKey: '500lbs_plus_resistance', subKey: 'handles_the_worst_chewers_and_pullers' },
    { Icon: IconBase, titleKey: 'wireless_charging', subKey: 'extend_wifi_connection_and_charge_anywhere' },
    { Icon: IconLight, titleKey: 'custom_light', subKey: 'keep_your_dog_visible_even_in_the_dark_no_period' },
    { Icon: IconBatteryCharging, titleKey: 'extended_battery_life', subKey: 'up_to_3_months_on_one_charge_no_period' },
    { Icon: IconMagic, titleKey: 'featuring_fi_intelligence', subKey: 'ask_fi_anything_your_dog_decoded' },
    { Icon: IconCellular, titleKey: 'att_lte_m_cellular_network', subKey: 'works_across_all_of_north_america' },
  ],
  ultra: [
    { Icon: IconVibration, titleKey: 'ultra_positive_reinforcement', subKey: 'ultra_positive_reinforcement_desc' },
    { Icon: IconCellular, titleKey: 'ultra_best_coverage', subKey: 'ultra_best_coverage_desc' },
    { Icon: IconDrip, titleKey: 'ultra_waterproof', subKey: 'ultra_waterproof_desc' },
    { Icon: IconBatteryCharging, titleKey: 'ultra_battery_life', subKey: 'ultra_battery_life_desc' },
    { Icon: IconConnected, titleKey: 'ultra_works_solo_or_paired', subKey: 'ultra_works_solo_or_paired_desc' },
  ],
};

export function HardwareFirstSpecs({ deviceKey }: { deviceKey: PickYourFiDeviceKey }) {
  const { t } = useTranslation();
  return (
    <section>
      <h2 className={styles.sectionHeading}>{t('hardware_first_device_specs', { device: DEVICE_NAMES[deviceKey] })}</h2>
      <div className={styles.specList}>
        {SPEC_ROWS[deviceKey].map((row) => (
          <div key={row.titleKey} className={styles.specRow}>
            <span className={styles.specIcon}>
              <row.Icon />
            </span>
            <span className={styles.specText}>
              <span className={styles.specTitle}>{t(row.titleKey)}</span>
              <span className={styles.specSub}>{t(row.subKey)}</span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

// feature lists mirror each device's standalone-pdp all-features section
const FEATURE_KEYS: Record<PickYourFiDeviceKey, string[]> = {
  mini: [
    'gps_lte_m_live_location_tracking',
    'escape_alerts_and_geofence',
    'activity_and_sleep_tracking',
    'barking_detection',
    'scratching_and_licking_tracking',
    'eating_and_drinking_tracking',
    'smart_vet_records',
    'walks_and_car_rides',
    'unlimited_users',
  ],
  s3: [
    'gps_lte_m_live_tracking',
    'geofence_escape_alerts',
    'activity_sleep_analysis',
    'barking_alerts',
    'scratching_licking_monitoring',
    'eating_drinking_detection',
    'smart_vet_records',
    'walks_car_rides',
    'night_light',
    'health_ai_agent',
    'unlimited_users',
  ],
  ultra: [
    'ultra_gps_tracking',
    'ultra_callback_technology',
    'ultra_escape_alerts',
    'ultra_vet_records',
    'ultra_walks_car_rides',
    'ultra_works_with_devices',
  ],
};

export function HardwareFirstAllFeatures({ deviceKey }: { deviceKey: PickYourFiDeviceKey }) {
  const { t } = useTranslation();
  return (
    <section className={styles.featuresCard}>
      <h2 className={styles.featuresHeading}>{t('hardware_first_all_features')}</h2>
      <div className={styles.featureList}>
        {FEATURE_KEYS[deviceKey].map((key) => (
          <div key={key} className={styles.featureRow}>
            <IconCheckCircle className={styles.featureCheck} />
            <span className={styles.featureLabel}>{t(key)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

const TESTIMONIALS = [
  {
    quoteKey: 'hardware_first_review_megan_quote',
    nameKey: 'hardware_first_review_megan_name',
    petKey: 'hardware_first_review_megan_pet',
  },
  { quoteKey: 'trial_testimonial_quote', nameKey: 'trial_testimonial_author' },
  { quoteKey: 'review_brian_quote', nameKey: 'hardware_first_review_brian_name' },
];

export function HardwareFirstOwnersSay() {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const onTrackScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    setActiveIndex(Math.round(track.scrollLeft / track.clientWidth));
  };

  const scrollToIndex = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
  };

  return (
    <section>
      <h2 className={styles.sectionHeading}>{t('hardware_first_what_owners_say')}</h2>
      <div className={styles.reviewTrack} ref={trackRef} onScroll={onTrackScroll}>
        {TESTIMONIALS.map((review) => (
          <div key={review.quoteKey} className={styles.reviewCard}>
            <div className={styles.reviewStars}>
              {Array.from({ length: 5 }).map((_, index) => (
                <IconStar key={index} className={styles.reviewStar} />
              ))}
            </div>
            <div className={styles.reviewQuote}>{t('hardware_first_quote_wrapped', { quote: t(review.quoteKey) })}</div>
            <div className={styles.reviewName}>{t(review.nameKey)}</div>
            {review.petKey && <div className={styles.reviewPet}>{t(review.petKey)}</div>}
          </div>
        ))}
      </div>
      <div className={styles.reviewDots}>
        {TESTIMONIALS.map((review, index) => (
          <button
            key={review.quoteKey}
            type="button"
            aria-label={t('rescue_testimonial_dot_label', { number: index + 1, total: TESTIMONIALS.length })}
            className={index === activeIndex ? styles.reviewDotActive : styles.reviewDot}
            onClick={() => scrollToIndex(index)}
          />
        ))}
      </div>
    </section>
  );
}

export function HardwareFirstQuizBand() {
  const { t } = useTranslation();
  const history = useHistory();
  const launchQuiz = useLaunchQuiz();

  return (
    <section className={styles.quizBand}>
      <div className={styles.quizEyebrow}>{t('compare_v2_quiz_eyebrow')}</div>
      <h2 className={styles.quizTitle}>{t('hardware_first_quiz_band_title')}</h2>
      <div className={styles.quizSubtitle}>{t('hardware_first_quiz_band_subtitle')}</div>
      <button
        type="button"
        className={styles.quizPrimary}
        onClick={() => {
          quizQuestionPage.quizButtonTapped();
          launchQuiz();
        }}
      >
        {t('take_the_quiz')}
      </button>
      <button
        type="button"
        className={styles.quizSecondary}
        onClick={() => {
          productListingPage.comparisonCompareProducts();
          history.push(AppPaths.Compare);
        }}
      >
        {t('compare_all_products')}
      </button>
    </section>
  );
}
